/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import { AnimatePresence, AnimationProps, motion } from 'framer-motion';
import { ReactNode, SVGProps, useEffect, useState, type FC } from 'react';
import { Button } from '../../Elements/Button';

import ChevronDown from '@icons/ChevronDown.svg';
import CloseIcon from '@icons/Close.svg';
import InfoOutlineIcon from '@icons/InfoOutline.svg';
import SuccessOutlineIcon from '@icons/SuccessOutline.svg';
import WarningOutlineIcon from '@icons/WarningOutline.svg';

export interface Props {
  /**
   * ClassName to be applied to the alert
   */
  className?: string;
  /**
   * Whether the alert is displayed
   */
  display?: boolean;
  /**
   * Time that the alert should be kept visible (in ms)
   */
  timeout?: number;
  /**
   * Title of the alert
   */
  title?: string;
  /**
   * Description text of the alert
   */
  description?: ReactNode;
  /**
   * Additionnal Content text of the alert
   */
  children?: ReactNode;
  /**
   * Theme of the alert
   */
  theme?: 'default' | 'info' | 'error' | 'warning' | 'success';
  /**
   * Custom icon component to display
   */
  icon?: FC<SVGProps<SVGElement>>;
  /**
   * Type of action button
   */
  actionButton?: 'toggle' | 'close';
  /**
   * Optional text to display beside the open button (Toggle action only)
   */
  openText?: string;
  /**
   * Optional text to display beside the close button (Toggle action only)
   */
  closeText?: string;
  /**
   * Callback to be called on the close button click (Close action only)
   */
  onClose?: () => void;
}

const Icon = ({ className = '', theme, icon: CustomIcon }: Props): ReactNode => {
  if (CustomIcon) {
    return <CustomIcon className={className} />;
  }
  switch (theme) {
    case 'info':
      return <InfoOutlineIcon className={className} />;
    case 'error':
    case 'warning':
      return <WarningOutlineIcon className={className} />;
    case 'success':
      return <SuccessOutlineIcon className={className} />;
    default:
      return;
  }
};

export const Alert = ({
  className = '',
  display,
  timeout,
  title,
  description,
  children,
  theme = 'default',
  icon,
  actionButton,
  closeText,
  openText,
  onClose,
}: Props) => {
  const [alertDisplay, setAlertDisplay] = useState(display);
  const [toggleDisplay, setToggleDisplay] = useState(false);

  useEffect(() => {
    setAlertDisplay(display);
    if (display && timeout) {
      const alertTimeout = setTimeout(() => {
        setAlertDisplay(false);
        setToggleDisplay(false);
      }, timeout);

      return () => {
        clearTimeout(alertTimeout);
      };
    }
  }, [display]);

  const closeCallback = () => {
    if (onClose) {
      onClose();
    }
    setAlertDisplay(false);
  };

  const alertMotionProps: AnimationProps = {
    initial: { opacity: 0, height: 'auto' },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
  };

  return (
    <div
      aria-hidden={!alertDisplay}
      role='alert'
      className={classNames({ 'invisible transition-all duration-500': !alertDisplay }, className)}
    >
      <AnimatePresence>
        {alertDisplay && (
          <motion.div
            {...alertMotionProps}
            className={classNames(
              'bg-surface-medium',
              'bg-gradient-to-r from-surface-medium to-surface-dark',
              'overflow-hidden',
              'font-sans text-md text-neutral-text-light',
              {
                'pr-2 pl-4': actionButton === 'close',
                'px-4': actionButton !== 'close',
              },
            )}
          >
            <div className='flex items-start justify-between gap-3 py-3 lg:items-center'>
              <Icon
                className={classNames('size-6 shrink-0', {
                  'text-neutral-text-xlight': theme === 'default',
                  'text-secondary-softlight': theme === 'info',
                  'text-feedback-error': theme === 'error',
                  'text-feedback-warning': theme === 'warning',
                  'text-feedback-success': theme === 'success',
                })}
                theme={theme}
                icon={icon}
              />
              <div
                className={classNames('flex grow items-start justify-start lg:items-center', {
                  'flex-col': actionButton === 'toggle',
                  'gap-3': actionButton === 'close',
                })}
              >
                <div className='flex flex-col'>
                  {title && <h3 className='font-semibold'>{title}</h3>}
                  {description && description}
                </div>
                {children &&
                  (actionButton === 'toggle' ? (
                    <AlertToggleContent display={toggleDisplay}>{children}</AlertToggleContent>
                  ) : (
                    children
                  ))}
              </div>
              {actionButton === 'toggle' && children && (
                <AlertToggleButton
                  display={toggleDisplay}
                  onClick={() => {
                    setToggleDisplay(!toggleDisplay);
                  }}
                >
                  {toggleDisplay ? closeText : openText}
                </AlertToggleButton>
              )}
              {actionButton === 'close' && <AlertCloseButton onClick={closeCallback} />}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AlertCloseButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button variant='plain' size='medium' icon={CloseIcon} onClick={onClick} className='opacity-70 hover:opacity-100' />
  );
};

const AlertToggleButton = ({
  children,
  display,
  onClick,
}: {
  children: ReactNode;
  display: boolean;
  onClick: () => void;
}) => {
  return (
    <Button
      variant='tertiary'
      size='xsmall'
      onClick={onClick}
      icon={ChevronDown}
      iconClassName={classNames('transition-all duration-300', {
        'rotate-180': display,
        'rotate-0': !display,
      })}
      iconPosition='after'
      aria-expanded={display}
      aria-controls='alertToggle'
    >
      {children}
    </Button>
  );
};

const AlertToggleContent = ({ children, display }: { children: ReactNode; display: boolean }) => {
  return (
    <div
      id='alertToggle'
      role='region'
      className={classNames('grid', 'overflow-hidden', 'transition-all', {
        'grid-rows-[0fr]': !display,
        'grid-rows-[1fr]': display,
      })}
    >
      <div
        className={classNames('min-h-0', 'transition-all', {
          'invisible -translate-y-full opacity-0': !display,
          'visible translate-y-0 opacity-100': display,
        })}
      >
        {children}
      </div>
    </div>
  );
};
